import React, { useEffect } from "react";
import { Box, Grid, styled } from "@mui/material";
import PageTitle from "../components/PageTitle";
import CartProductTable from "../components/Cart/CartProductTable";
import { CartPayload, CartItem, PurchaseOrderForm, LooseItem } from "../types";

import CartPurchaseOrderDetails from "../components/Cart/CartPurchaseOrderDetails";
import CartDownload from "../components/Cart/CartDownload";
import { useAppDispatch } from "../hooks";
import { incrementRecount } from "../reducers/products";


const CartContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,

  overflowY: "auto",

  display: "flex",
  flexDirection: "column",

  height: "calc(100vh - 72px - 65px)",

  margin: "0 auto",
  padding: "16px 96px",
  gap: "16px",

  [theme.breakpoints.down("md")]: {
    padding: "12px 12px 72px",
  },
}));

const Cart: React.FC = () => {
  const [items, setItems] = React.useState<CartItem[]>([]);
  const [looseItems, setLooseItems] = React.useState<LooseItem[]>([]);
  const [purchaseOrderDetails, setPurchaseOrderDetails] =
    React.useState<PurchaseOrderForm | null>(null);
  const [isValid, setIsValid] = React.useState<boolean>(false);

  const isHighScaling = window.devicePixelRatio >= 1.5;

  const dispatch = useAppDispatch();

  const handleUpdateQuantity = (id: string, quantity: number) => {
    const newItems = items.map((item) => {
      if (item.id === id) {
        item.configuration.count = quantity;

        return { ...item };
      }
      return item;
    });
    setItems(newItems);

    const newLooseItems = looseItems.map((item) => {
      if (item.id === id) {
        item.count = quantity;

        return { ...item };
      }
      return item;
    });
    setLooseItems(newLooseItems);

    const updatedCart: CartPayload = {
      items: newItems,
      looseItems: newLooseItems,
    };
    localStorage.setItem("Cart", JSON.stringify(updatedCart));
  };

  const removeProduct = (id: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== id));
    setLooseItems((prevItems) => prevItems.filter((item) => item.id !== id));

    const updatedCart: CartPayload = {
      items: items.filter((item) => item.id !== id),
      looseItems: looseItems.filter((item) => item.id !== id),
    };

    localStorage.setItem("Cart", JSON.stringify(updatedCart));
    dispatch(incrementRecount());
  };

  useEffect(() => {
    const savedData = localStorage.getItem("Cart");

    if (savedData) {
      const parsedData: CartPayload = JSON.parse(savedData);
      setItems(parsedData.items || []);
      setLooseItems(parsedData.looseItems || []);
    }
  }, []);

  const analyticsPairs = [
    ...items.map((item) => ({
      productId: item.configuration.productId,
      modelId: item.baseProduct.configurationProductId,
      quantity: item.configuration.count,
    })),
    ...looseItems.map((item) => ({
      productId: item.id,
      modelId: "",
      quantity: item.count,
    })),
  ].slice(0, 6);

  const productAmount = items.length + looseItems.length;

  const eventIds = analyticsPairs.length > 0 ? {
    productIds: analyticsPairs.map((pair) => pair.productId),
    modelIds: analyticsPairs.map((pair) => pair.modelId),
    productQuantities: analyticsPairs.map((pair) => pair.quantity),
    productAmount,
  } : null;

  return (
    <CartContainer component="main">
      <PageTitle
        title="Cart"
        titleId="tableTitle"
        subtitle="See your configurations and data."
      />

      <Grid container spacing="16px">
        <Grid item xs={isHighScaling ? 12 : 6}>
          <CartProductTable
            rowsItems={items}
            rowsLooseItems={looseItems}
            handleUpdateQuantity={handleUpdateQuantity}
            removeProduct={removeProduct}
          />
        </Grid>

        <Grid item xs={isHighScaling ? 12 : 6}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <CartPurchaseOrderDetails
              setIsValid={setIsValid}
              onChange={setPurchaseOrderDetails}
            />

            <CartDownload
              looseParts={looseItems}
              configuration={items.map((i) => i.configuration)}
              purchaseOrderDetails={purchaseOrderDetails}
              eventIds={eventIds}
              isValid={isValid}
            />
          </Box>
        </Grid>
      </Grid>
    </CartContainer>
  );
};

export default Cart;
